import React from "react";
import { observer } from "mobx-react";
import { computed } from "mobx";
import { generatePath, withRouter, RouteComponentProps } from "react-router";
import InfiniteScroll from "react-infinite-scroll-component";

import AddIcon from "@material-ui/icons/Add";
import { Button } from "@material-ui/core";

import ContentResourcesQuickLinksStore from "stores/ContentResourcesQuickLinksStore";

import ConfirmationDialog from "components/ConfirmationDialog";
import Page from "components/Layout/Page";
import Placeholder from "components/Placeholder";
import RestoreArchivedButton from "components/RestoreArchivedButton";
import FilteringControl from "components/FilteringControl";
import SortingControl from "components/SortingControl";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";
import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import NavBar from "../NavBar";
import QuickLinkCard from "./QuickLinkCard";

import { SNACKBAR_TYPES } from "components/Snackbar";

import ROUTES from "navigation/routes";

import { TableSortType } from "stores/templates/TableStore";

import { getConfirmArchivationText } from "utils/publish";

import {
  PageControl,
  SearchContainer,
  SearchControls,
  SearchControlWrapper,
  Search,
  QuickLinkCardWrapper,
  OverflowLoader,
  ScrollLoader,
} from "./styles";

type Props = RouteComponentProps & GlobalPopupProps & SnackbarContextProps & {};

const HEADER_WITH_TITLE_HEIGHT = "338px";
const MINIMUM_SEARCH_VALUE_LENGTH = 3;

@observer
class QuickLinks extends React.Component<Props> {
  @computed get quickLinks() {
    return ContentResourcesQuickLinksStore.items;
  }

  @computed get quickLinkListIsEmpty() {
    return this.quickLinks.length === 0;
  }

  componentDidMount() {
    ContentResourcesQuickLinksStore.fetch();
  }

  componentWillUnmount() {
    ContentResourcesQuickLinksStore.resetPage();
  }

  fetchMoreQuickLinks = async () => {
    const { setMessage } = this.props;

    try {
      await ContentResourcesQuickLinksStore.fetchMoreQuickLinks();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onSearchApply = (value: string) => {
    if (value.length === 0) {
      this.onSearchClear();
      return;
    }

    ContentResourcesQuickLinksStore.applySearch(value);
    value.length >= MINIMUM_SEARCH_VALUE_LENGTH &&
      ContentResourcesQuickLinksStore.fetch();
  };

  onSearchClear = () => {
    ContentResourcesQuickLinksStore.clearSearch();
    ContentResourcesQuickLinksStore.fetch();
  };

  onSortApply = (value: TableSortType) => {
    ContentResourcesQuickLinksStore.applyOrder(value);
    ContentResourcesQuickLinksStore.resetPage();
    ContentResourcesQuickLinksStore.fetch();
  };

  onFilterApply = (
    filterValues: { column: string; value: (string | number)[] }[],
  ) => {
    filterValues.map(filterValue =>
      ContentResourcesQuickLinksStore.applyFilter({
        column: filterValue.column,
        value: filterValue.value,
      }),
    );
    ContentResourcesQuickLinksStore.resetPage();
    ContentResourcesQuickLinksStore.fetch();
  };

  onFilterClear = () => {
    ContentResourcesQuickLinksStore.clearFilters();
    ContentResourcesQuickLinksStore.fetch();
  };

  onAddQuickLink = () => {
    this.props.history.push({
      pathname: generatePath(ROUTES.CONTENT_RESOURCES_QUICK_LINK_DETAILS),
      state: { editMode: true },
    });
  };

  onDetailsClick = (quickLink: any) => () => {
    const path = generatePath(ROUTES.CONTENT_RESOURCES_QUICK_LINK_DETAILS, {
      quickLinkId: quickLink.id,
    });
    this.props.history.push({ pathname: path, state: { editMode: false } });
  };

  onEdit = (quickLink: any) => () => {
    const path = generatePath(ROUTES.CONTENT_RESOURCES_QUICK_LINK_DETAILS, {
      quickLinkId: quickLink.id,
    });
    this.props.history.push({ pathname: path, state: { editMode: true } });
  };

  onPublish = (quickLink: any) => async () => {
    const { setMessage } = this.props;
    const { id, publishStatus } = quickLink;

    try {
      await ContentResourcesQuickLinksStore.updatePublishStatus(
        id,
        publishStatus,
      );
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onArchive = (quickLink: any) => () => {
    const { openPopup } = this.props;

    const quickLinkId = quickLink.id;

    const text = getConfirmArchivationText({
      entity: quickLink,
      suffix: "quick link",
    });

    openPopup({
      key: "ArchiveQuickLink",
      canCloseByOuterClick: true,
      withoutExitButton: true,
      render: ({ close }) => (
        <ConfirmationDialog
          title={"Archive Quick Link"}
          text={text}
          acceptText={"Archive"}
          declineText={"Cancel"}
          isLoading={ContentResourcesQuickLinksStore.isQuickLinkManaging}
          onDecline={close}
          onAccept={this.archiveQuickLink(quickLinkId, close)}
        />
      ),
    });
  };

  archiveQuickLink = (id: number, close: () => void) => async () => {
    const { setMessage } = this.props;

    try {
      await ContentResourcesQuickLinksStore.archiveQuickLink(id);
      close();
      ContentResourcesQuickLinksStore.resetPage();
      ContentResourcesQuickLinksStore.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onCopy = (quickLink: any) => async () => {
    const { setMessage } = this.props;
    const id = quickLink.id;

    try {
      await ContentResourcesQuickLinksStore.copyQuickLink(id);
      ContentResourcesQuickLinksStore.resetPage();
      ContentResourcesQuickLinksStore.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  render() {
    return (
      <Page
        title="Quick Links"
        topControls={<NavBar />}
        controls={
          <>
            <PageControl>
              <RestoreArchivedButton />
            </PageControl>
            <PageControl>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={this.onAddQuickLink}
              >
                Add Quick Link
              </Button>
            </PageControl>
          </>
        }
        isLoading={ContentResourcesQuickLinksStore.isLoading}
      >
        <SearchContainer>
          <Search
            initValue={ContentResourcesQuickLinksStore.search}
            onApply={this.onSearchApply}
            onClear={this.onSearchClear}
          />
          <SearchControls>
            <SearchControlWrapper>
              <SortingControl
                initValue={
                  ContentResourcesQuickLinksStore.orderContext.appliedOrderInfo
                }
                onApply={this.onSortApply}
              />
            </SearchControlWrapper>
            <SearchControlWrapper>
              <FilteringControl
                initValue={ContentResourcesQuickLinksStore.filters}
                onApply={this.onFilterApply}
                onClear={this.onFilterClear}
                statusFiltering
              />
            </SearchControlWrapper>
          </SearchControls>
        </SearchContainer>

        {(ContentResourcesQuickLinksStore.isQuickLinkPublishing ||
          ContentResourcesQuickLinksStore.isQuickLinkCopying) && (
          <OverflowLoader />
        )}
        {!this.quickLinkListIsEmpty ? (
          <InfiniteScroll
            dataLength={this.quickLinks.length}
            hasMore={ContentResourcesQuickLinksStore.hasMoreInfo}
            next={this.fetchMoreQuickLinks}
            height={`calc(100vh - ${HEADER_WITH_TITLE_HEIGHT})`}
            loader={<ScrollLoader />}
          >
            {this.quickLinks.map(quickLink => (
              <QuickLinkCardWrapper key={quickLink.id}>
                <QuickLinkCard
                  title={quickLink.title}
                  duration={quickLink.durationText}
                  image={quickLink.imageUrl}
                  publishStatus={quickLink.publishStatus}
                  onDetailsClick={this.onDetailsClick(quickLink)}
                  onArchive={this.onArchive(quickLink)}
                  onEdit={this.onEdit(quickLink)}
                  onCopy={this.onCopy(quickLink)}
                  onPublish={this.onPublish(quickLink)}
                />
              </QuickLinkCardWrapper>
            ))}
          </InfiniteScroll>
        ) : (
          <Placeholder
            height={280}
            width={280}
            text={
              ContentResourcesQuickLinksStore.search
                ? "No matches found. Please try another search."
                : "No quick links added yet."
            }
          />
        )}
      </Page>
    );
  }
}

export default withGlobalPopup(withGlobalSnackbar(withRouter(QuickLinks)));
